import { useSyncExternalStore } from 'react';
import { ALL_SIDES, type SideOption } from '../utils/guestSide';

/**
 * State shared between the Guests breadcrumb bar (Header) and the Guests list: the Archived switch and the Filters
 * popover choices (the page search is the global header search, not a box of its own), plus what the bar needs from the list to draw itself (the side and tag choices that
 * exist, how many duplicate groups there are). A tiny external store, so neither App nor Header needs new props.
 */
export interface GuestsBarState {
  archived: boolean;
  side: string;
  rsvp: string[];
  tags: string[];
  roles: string[];
  /** Only guests with at least one per-event answer still pending. */
  anyPending: boolean;
  sideOptions: SideOption[];
  tagOptions: Array<{ value: string; label: string }>;
  duplicateGroups: number;
}

export const GUEST_MERGE_OPEN = 'guest-merge-open';
export const GUEST_BULK_ADD_OPEN = 'guest-bulk-add-open';

const INITIAL: GuestsBarState = {
  archived: false, side: ALL_SIDES, rsvp: [], tags: [], roles: [], anyPending: false,
  sideOptions: [{ value: ALL_SIDES, label: 'All sides' }], tagOptions: [], duplicateGroups: 0,
};

let state: GuestsBarState = INITIAL;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

export const getGuestsBar = (): GuestsBarState => state;

export function setGuestsBar(patch: Partial<GuestsBarState>): void {
  state = { ...state, ...patch };
  emit();
}

/** Back to no filters (keeps nothing from the previous visit). */
export function resetGuestsBar(): void {
  state = INITIAL;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function useGuestsBar(): GuestsBarState {
  return useSyncExternalStore(subscribe, getGuestsBar, getGuestsBar);
}
